import { useCallback, useRef, useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useExploreTradeoffs } from '@backrs_ui/grow-ui/src/pages/Result/hooks/useExploreTradeoffs';
import { blueprintLubavApi } from '@backrs_ui/grow-ui/src/redux/blueprint/blueprint.lubav.api';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { RootState } from '@backrs_ui/grow-ui/src/redux/store';
import { closeCustomTab } from '@backrs_ui/grow-ui/src/redux/tabs/tabs.slice';
import {
  toCareerPathResults,
  updateResponseSetOnAPI,
} from '@backrs_ui/grow-ui/src/redux/userResponse/thunks/updateResponseSet.thunk';
import {
  addUserSurveyResults,
  selectMostRecentUserResponseSet,
} from '@backrs_ui/grow-ui/src/redux/userResponse/userResponse';
import { determineOwnershipLevel } from '@backrs_ui/grow-ui/src/utils/determineOwnership';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

import { AspirationAccordion } from './AspirationAccordion';
import { useAgentContext } from './ExploreTradeoffsPanel.agentContext';
import { LockInConfirmationModal } from './LockInConfirmationModal';
import { TradeoffConnectionLines } from './TradeoffConnectionLines';
import { RequirementSectionKey, TradeoffResultsPanel } from './TradeoffResultsPanel';
import { ExploreTradeoffsCard } from '../ExploreTradeoffsCard';

const SECTION_STRINGS = appStrings.modules.survey.output.blueprintSection;
const STRINGS = SECTION_STRINGS.exploreTradeoffsView;

export function ExploreTradeoffsPanel() {
  const dispatch = useAppDispatch();
  const careerQuestions = useAppSelector((state: RootState) => state.survey.careerQuestions);
  const userResponseSet = useAppSelector(selectMostRecentUserResponseSet);

  if (!userResponseSet) {
    throw new Error('ExploreTradeoffsPanel rendered without a loaded user response set');
  }

  const {
    draftResponseSet,
    draftTier,
    draftCareerPathSuggestions,
    draftCareerResults,
    isDirty,
    updateAnswer,
    markClean,
  } = useExploreTradeoffs(userResponseSet);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);
  const [hoveredCategoryKey, setHoveredCategoryKey] = useState<string | null>(null);

  const panelsRef = useRef<HTMLDivElement>(null);
  const aspirationRefs = useRef<Map<string, HTMLElement>>(new Map());
  const requirementRefs = useRef<Map<RequirementSectionKey, HTMLElement>>(new Map());

  const registerAspirationRef = useCallback((key: string, el: HTMLElement | null) => {
    if (el) {
      aspirationRefs.current.set(key, el);
    } else {
      aspirationRefs.current.delete(key);
    }
  }, []);

  const registerRequirementRef = useCallback(
    (key: RequirementSectionKey, el: HTMLElement | null) => {
      if (el) {
        requirementRefs.current.set(key, el);
      } else {
        requirementRefs.current.delete(key);
      }
    },
    [],
  );

  const draftOwnershipLevel = determineOwnershipLevel(draftTier, draftResponseSet, careerQuestions);

  useAgentContext(isDirty);

  const handleLockIn = async () => {
    setIsLocking(true);

    try {
      const mappedCareerPaths = toCareerPathResults(draftCareerPathSuggestions);

      await dispatch(
        updateResponseSetOnAPI({
          responseSetId: userResponseSet.id,
          userResponseSet: draftResponseSet,
          tier: draftTier,
          entrepreneurshipRecommended: draftCareerResults.entrepreneurship,
          careerPathResults: mappedCareerPaths,
          ownershipLevel: draftOwnershipLevel,
        }),
      ).unwrap();
    } catch (error) {
      console.error('Lock In failed:', error);
      setLockError(
        typeof error === 'string'
          ? error
          : error instanceof Error
            ? error.message
            : STRINGS.lockInError,
      );
      setIsLocking(false);

      return;
    }

    try {
      dispatch(
        addUserSurveyResults({
          tier: draftTier,
          careerPathSuggestions: draftCareerPathSuggestions,
          careerResults: draftCareerResults,
        }),
      );

      // Invalidate aspirations categories cache so the LLM-generated summaries regenerate
      dispatch(blueprintLubavApi.util.invalidateTags(['AspirationsCategories']));

      // Reset dirty baseline so the Lock In CTA hides
      markClean();

      setShowConfirmModal(false);
      dispatch(closeCustomTab('tradeoffs'));
    } catch (error) {
      console.error('Post-lock-in state update failed:', error);
    } finally {
      setIsLocking(false);
    }
  };

  const inlineAction = (
    <Button
      id="tradeoff-lock-in-btn"
      hierarchy="Secondary gray"
      size="md"
      icon="Leading"
      startIcon={
        <AppIcon iconName="Lock01" diameter="20px" color="Colors/Foreground/fg-secondary" />
      }
      disabled={!isDirty}
      onClick={() => {
        setLockError(null);
        setShowConfirmModal(true);
      }}
      sx={lockInBtnSx}
    >
      {STRINGS.lockInCta}
    </Button>
  );

  return (
    <Box sx={containerSx}>
      <Box sx={cardWrapperSx}>
        <ExploreTradeoffsCard
          title={SECTION_STRINGS.exploreTradeoffsCard.expanded.title}
          description={SECTION_STRINGS.exploreTradeoffsCard.expanded.description}
          inlineAction={inlineAction}
        />
      </Box>

      <Box ref={panelsRef} sx={panelsSx}>
        <Box sx={leftPanelSx}>
          <Typography variant="text-sm-bold" sx={panelTitleSx}>
            {STRINGS.leftPanelTitle}
          </Typography>
          <AspirationAccordion
            draftResponseSet={draftResponseSet}
            careerQuestions={careerQuestions}
            onAnswerChange={updateAnswer}
            registerCategoryRef={registerAspirationRef}
            onCategoryHeaderHover={setHoveredCategoryKey}
          />
        </Box>

        <Box sx={rightPanelSx}>
          <Typography variant="text-sm-bold" sx={panelTitleSx}>
            {STRINGS.rightPanelTitle}
          </Typography>
          <TradeoffResultsPanel
            draftTier={draftTier}
            draftCareerPathSuggestions={draftCareerPathSuggestions}
            ownershipLevel={draftOwnershipLevel}
            registerSectionRef={registerRequirementRef}
          />
        </Box>

        <TradeoffConnectionLines
          containerRef={panelsRef}
          aspirationRefs={aspirationRefs}
          requirementRefs={requirementRefs}
          hoveredCategoryKey={hoveredCategoryKey}
        />
      </Box>

      <LockInConfirmationModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleLockIn}
        isLoading={isLocking}
        error={lockError}
      />
    </Box>
  );
}

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  px: backrs.spacing['spacing-md'],
  pb: backrs.spacing['spacing-md'],
  containerType: 'inline-size',
});

const cardWrapperSx: SxProps<Theme> = {
  position: 'relative',
  zIndex: 1,
};

const panelsSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  display: 'grid',
  gap: backrs.spacing['spacing-2xl'],
  marginTop: backrs.spacing['spacing-2xl'],
  gridTemplateColumns: '1fr',
  '@container (min-width: 600px)': {
    gap: backrs.spacing['spacing-6xl'],
    gridTemplateColumns: '45fr 55fr',
  },
});

const leftPanelSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const rightPanelSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const panelTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/700'],
  textTransform: 'uppercase',
});

const lockInBtnSx: SxProps<Theme> = {
  alignSelf: 'stretch',
  '@container (min-width: 600px)': {
    alignSelf: 'auto',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
};
